/**
 * Procedural sound effects via the Web Audio API.
 *
 * There are no audio files: every sound is synthesised at runtime from
 * oscillators + filtered noise. That keeps the offline PWA precache light (no
 * MP3/OGG weight), needs no licensing, and lets each cue be tuned in code. It's
 * strictly best-effort — if the browser has no AudioContext, blocks autoplay, or
 * anything throws mid-build, the call degrades to silence and never bubbles an
 * error into gameplay.
 *
 * Mute is persisted to localStorage so the player's choice survives reloads.
 */

const STORAGE_KEY = 'dc_muted';

export type SfxName =
  | 'shot_ranged'
  | 'shot_magic'
  | 'shot_support'
  | 'hit'
  | 'crit'
  | 'death'
  | 'leak'
  | 'unit_death'
  | 'unit_respawn'
  | 'deploy'
  | 'upgrade'
  | 'sell'
  | 'click'
  | 'wave_start'
  | 'victory'
  | 'defeat';

/** Minimum gap (s) between repeats of a sound, so bursty events (shots, hits,
 *  deaths at 2×/3× speed) thin out into texture instead of a wall of noise. */
const THROTTLE: Partial<Record<SfxName, number>> = {
  shot_ranged: 0.045,
  shot_magic: 0.045,
  shot_support: 0.05,
  hit: 0.035,
  crit: 0.05,
  death: 0.035,
  leak: 0.1,
};

interface ToneOpts {
  freq: number;
  /** Glide target by the end of the sound (exponential). */
  to?: number;
  dur: number;
  type?: OscillatorType;
  gain?: number;
  attack?: number;
  /** Start offset from now (s) — used to sequence arpeggios/fanfares. */
  delay?: number;
}

interface NoiseOpts {
  dur: number;
  gain?: number;
  type?: BiquadFilterType;
  freq?: number;
  to?: number;
  q?: number;
}

class SfxEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noiseBuf: AudioBuffer | null = null;
  private muted = false;
  /** Last play time (ctx clock) per sound, for throttling. */
  private last: Record<string, number> = {};

  constructor() {
    try {
      this.muted = globalThis.localStorage?.getItem(STORAGE_KEY) === '1';
    } catch {
      /* localStorage unavailable — default to unmuted */
    }
  }

  // ---- public API ----------------------------------------------------------

  isMuted(): boolean {
    return this.muted;
  }

  setMuted(m: boolean): void {
    this.muted = m;
    try {
      globalThis.localStorage?.setItem(STORAGE_KEY, m ? '1' : '0');
    } catch {
      /* ignore */
    }
  }

  toggleMuted(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  play(name: SfxName): void {
    try {
      if (!this.ensureCtx()) return;
      const gap = THROTTLE[name];
      if (gap != null && !this.allow(name, gap)) return;
      this.build(name);
    } catch {
      /* best-effort: never let audio break the game */
    }
  }

  // ---- context lifecycle ---------------------------------------------------

  /** Lazily create the AudioContext (must happen inside a user gesture the first
   *  time, which it does — the first cue is a menu/HUD click). Returns false if
   *  muted or audio is unavailable. */
  private ensureCtx(): boolean {
    if (this.muted) return false;
    if (!this.ctx) {
      const AC =
        (globalThis as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
          .AudioContext ??
        (globalThis as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AC) return false;
      this.ctx = new AC();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.7; // headroom for overlapping voices
      this.master.connect(this.ctx.destination);
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    return true;
  }

  private allow(name: string, gap: number): boolean {
    const t = this.ctx!.currentTime;
    if ((this.last[name] ?? -Infinity) > t - gap) return false;
    this.last[name] = t;
    return true;
  }

  // ---- synthesis primitives ------------------------------------------------

  private tone(o: ToneOpts): void {
    const ctx = this.ctx!;
    const t = ctx.currentTime + (o.delay ?? 0);
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = o.type ?? 'sine';
    osc.frequency.setValueAtTime(o.freq, t);
    if (o.to != null) osc.frequency.exponentialRampToValueAtTime(Math.max(1, o.to), t + o.dur);
    const peak = o.gain ?? 0.3;
    const a = o.attack ?? 0.005;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + a);
    g.gain.exponentialRampToValueAtTime(0.0001, t + o.dur);
    osc.connect(g).connect(this.master!);
    osc.start(t);
    osc.stop(t + o.dur + 0.03);
  }

  private noise(o: NoiseOpts): void {
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const src = ctx.createBufferSource();
    src.buffer = this.getNoise();
    const filter = ctx.createBiquadFilter();
    filter.type = o.type ?? 'bandpass';
    filter.frequency.setValueAtTime(o.freq ?? 1000, t);
    if (o.to != null) filter.frequency.exponentialRampToValueAtTime(Math.max(1, o.to), t + o.dur);
    filter.Q.value = o.q ?? 1;
    const g = ctx.createGain();
    const peak = o.gain ?? 0.3;
    g.gain.setValueAtTime(peak, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + o.dur);
    src.connect(filter).connect(g).connect(this.master!);
    src.start(t);
    src.stop(t + o.dur + 0.03);
  }

  /** One reusable second of white noise — new BufferSources read from it, so we
   *  don't regenerate noise on every impact. */
  private getNoise(): AudioBuffer {
    if (!this.noiseBuf) {
      const ctx = this.ctx!;
      const frames = ctx.sampleRate;
      const buf = ctx.createBuffer(1, frames, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < frames; i++) d[i] = Math.random() * 2 - 1;
      this.noiseBuf = buf;
    }
    return this.noiseBuf;
  }

  // ---- the sound catalogue -------------------------------------------------

  private build(name: SfxName): void {
    // Small per-shot pitch wobble so repeated cues feel organic, not looped.
    const v = 0.9 + Math.random() * 0.2;
    switch (name) {
      case 'shot_ranged': // arrow whoosh — a quick filtered-noise sweep
        this.noise({ dur: 0.12, gain: 0.1, type: 'bandpass', freq: 2200 * v, to: 600, q: 0.8 });
        break;
      case 'shot_magic': // arcane zap — sawtooth dive with a bright overtone
        this.tone({ freq: 760 * v, to: 180, dur: 0.16, type: 'sawtooth', gain: 0.1 });
        this.tone({ freq: 1500 * v, to: 360, dur: 0.1, type: 'square', gain: 0.035 });
        break;
      case 'shot_support': // soft mote chime
        this.tone({ freq: 880 * v, dur: 0.14, type: 'triangle', gain: 0.09 });
        this.tone({ freq: 1320 * v, dur: 0.12, type: 'sine', gain: 0.045, delay: 0.02 });
        break;
      case 'hit': // dull connect
        this.tone({ freq: 180 * v, to: 90, dur: 0.07, type: 'triangle', gain: 0.09 });
        this.noise({ dur: 0.05, gain: 0.045, type: 'lowpass', freq: 1200 });
        break;
      case 'crit': // bright zing on a crit
        this.tone({ freq: 1200, to: 1750, dur: 0.08, type: 'square', gain: 0.07 });
        this.tone({ freq: 1950, dur: 0.1, type: 'sine', gain: 0.06, delay: 0.04 });
        break;
      case 'death': // enemy shatters — crunchy noise + a falling body
        this.noise({ dur: 0.22, gain: 0.14, type: 'lowpass', freq: 1800 * v, to: 200, q: 1 });
        this.tone({ freq: 200 * v, to: 60, dur: 0.2, type: 'sawtooth', gain: 0.06 });
        break;
      case 'leak': // the gate is breached — low ominous thud
        this.tone({ freq: 120, to: 50, dur: 0.4, type: 'square', gain: 0.16 });
        this.noise({ dur: 0.3, gain: 0.09, type: 'lowpass', freq: 400, to: 120 });
        break;
      case 'unit_death': // a defender falls
        this.tone({ freq: 300, to: 110, dur: 0.35, type: 'triangle', gain: 0.11 });
        break;
      case 'unit_respawn': // rallied — a rising triad
        [440, 660, 880].forEach((f, i) =>
          this.tone({ freq: f, dur: 0.16, type: 'triangle', gain: 0.08, delay: i * 0.06 }),
        );
        break;
      case 'deploy': // confirming place-down
        this.tone({ freq: 160, to: 320, dur: 0.12, type: 'square', gain: 0.13 });
        this.tone({ freq: 480, dur: 0.1, type: 'triangle', gain: 0.07, delay: 0.06 });
        break;
      case 'upgrade': // ascending sparkle
        [523, 659, 784, 1047].forEach((f, i) =>
          this.tone({ freq: f, dur: 0.16, type: 'triangle', gain: 0.09, delay: i * 0.05 }),
        );
        break;
      case 'sell': // coins clink back
        [1175, 880].forEach((f, i) =>
          this.tone({ freq: f, dur: 0.12, type: 'square', gain: 0.08, delay: i * 0.05 }),
        );
        break;
      case 'click': // soft UI tick
        this.tone({ freq: 660, to: 880, dur: 0.05, type: 'triangle', gain: 0.07 });
        break;
      case 'wave_start': // a war-horn swell as the wave marches
        this.tone({ freq: 220, to: 330, dur: 0.5, type: 'sawtooth', gain: 0.12 });
        this.tone({ freq: 330, to: 440, dur: 0.5, type: 'sawtooth', gain: 0.07, delay: 0.12 });
        break;
      case 'victory': // ascending fanfare
        [523, 659, 784, 1047, 1319].forEach((f, i) =>
          this.tone({ freq: f, dur: 0.5, type: 'triangle', gain: 0.12, delay: i * 0.12 }),
        );
        break;
      case 'defeat': // descending lament
        [440, 349, 262].forEach((f, i) =>
          this.tone({ freq: f, dur: 0.55, type: 'sawtooth', gain: 0.12, delay: i * 0.16 }),
        );
        break;
    }
  }
}

/** Shared singleton — the whole game routes through one mixer/mute. */
export const sfx = new SfxEngine();
